// Motor de búsqueda inteligente: insensible a mayúsculas y acentos, multi-término
// (los términos pueden ir en cualquier orden) y tolerante a errores de tecleo (fuzzy).

export function normalize(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Distancia de Levenshtein con corte temprano.
function levenshtein(a, b, max) {
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  let prev = Array.from({ length: lb + 1 }, (_, i) => i);
  let curr = new Array(lb + 1);
  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[lb];
}

function allowedEdits(token) {
  if (token.length <= 3) return 0;
  if (token.length <= 6) return 1;
  return 2;
}

// Devuelve un puntaje para un término contra el texto y sus palabras.
// 0 = no coincide. Mayor = mejor relevancia.
function scoreTerm(haystack, words, token) {
  const max = allowedEdits(token);
  const idx = haystack.indexOf(token);
  if (idx === 0) return 4;            // el texto empieza con el término
  if (idx > 0) {
    // bonus si empieza una palabra
    const prev = haystack[idx - 1];
    return prev === " " ? 3 : 2;
  }
  if (max > 0) {
    for (const w of words) {
      if (w.length < 3) continue;
      if (Math.abs(w.length - token.length) > max) continue;
      if (levenshtein(w, token, max) <= max) return 1;
    }
  }
  return 0;
}

function fieldValues(item, fields) {
  if (typeof fields === "function") return fields(item);
  return fields.map((f) => item?.[f]);
}

// Comprueba si un conjunto de valores coincide con la consulta (AND multi-término).
export function matchesQuery(values, query) {
  const q = normalize(query);
  if (!q) return true;
  const haystack = values.map((v) => normalize(v)).filter(Boolean).join(" ");
  if (!haystack) return false;
  const words = haystack.split(" ");
  const terms = q.split(" ").filter(Boolean);
  return terms.every((t) => scoreTerm(haystack, words, t) > 0);
}

// Filtra + ordena por relevancia. `fields` = array de claves o función que
// devuelve un array de valores.
export function smartSearch(items, query, fields) {
  const q = normalize(query);
  if (!q) return items;
  const terms = q.split(" ").filter(Boolean);
  const scored = [];
  items.forEach((item, i) => {
    const values = fieldValues(item, fields);
    const haystack = values.map((v) => normalize(v)).filter(Boolean).join(" ");
    if (!haystack) return;
    const words = haystack.split(" ");
    let total = 0;
    let ok = true;
    for (const t of terms) {
      const sc = scoreTerm(haystack, words, t);
      if (sc === 0) { ok = false; break; }
      total += sc;
    }
    if (ok) scored.push({ item, total, i });
  });
  scored.sort((a, b) => (b.total - a.total) || (a.i - b.i));
  return scored.map((s) => s.item);
}

// Convierte "dd/mm/yyyy" o ISO a "yyyy-mm-dd" para comparaciones de rango.
export function toISODate(v) {
  if (!v) return "";
  const s = String(v);
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// Filtro combinado: búsqueda inteligente + rango de fechas + igualdades (vendedor, estado…).
export function filterRows(rows, { q, fields, dateField, from, to, equals } = {}) {
  let out = q && q.trim() ? smartSearch(rows, q, fields) : rows;
  if (from || to) {
    out = out.filter((r) => {
      const d = toISODate(r[dateField] || r.date || r.date_iso || r.created_at);
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }
  if (equals) {
    for (const { field, value } of equals) {
      if (value && value !== "all") out = out.filter((r) => (r[field] ?? "") === value);
    }
  }
  return out;
}
